<template>
  <div v-if="is_loading_data" class="is-flex is-justify-content-center is-align-items-center is-flex-direction-column">
    <div class="loading"></div>
    <p>טוען מידע על ה- Offer...</p>
  </div>
  <div v-else>
    <div v-if="isSendOneFile" class="text_align_center red_text bold">
      <span>ניתן לשלוח לחברה זו קובץ אחד בלבד</span>
    </div>
    <CvOfferAlerts @setRedAlert="setRedAlert($event)" @warningsStatus="warningsStatus" :offer="cv_offer" />

    <!-- Send CV Mode Selector - Hidden in Super Lite Form -->
    <!-- Super Lite form should not show mode selector as it's already in super lite mode -->

    <div v-if="!is_submitted">
      <StartVetting v-if="cv_offer && !['vetted', 'completed', 'irrelevant'].includes(cv_offer.applicant.applicant_status)"
                     :payload="cv_offer.applicant" />
      <div dir="ltr" style="text-align: left;">
        <Gender :applicantId="cv_offer.applicant.resource_id" v-model="applicantGender" />
        <!-- Enhanced Abstract Selection Section -->
        <div class="enhanced-abstract-selection">
          <div class="selection-header">
            <h3 class="selection-title">
              <i class="fas fa-file-alt"></i>
              בחר סוג אבסטרקט לשליחה
            </h3>
          </div>

          <div class="abstract-type-cards">
            <!-- Regular Abstract Card -->
            <div
              class="abstract-card"
              :class="{ 'is-selected': selectedAbstractType === 'regular' }"
              @click="selectAbstractType('regular')"
            >
              <div class="card-header">
                <div class="card-title">
                  <span>אבסטרקט רגיל</span>
                  <i class="fas fa-file-text"></i>
                </div>
              </div>
            </div>

            <!-- Mixed Abstract Card -->
            <div
              class="abstract-card"
              :class="{ 'is-selected': selectedAbstractType === 'mixed' }"
              @click="selectAbstractType('mixed')"
            >
              <div class="card-header">
                <div class="card-title">
                  <span>אבסטרקט חסוי</span>
                  <i class="fas fa-magic"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Abstract Fields Section -->
        <div class="abstract-fields-section">
          <!-- Regular Abstract Field -->
          <div class="abstract-field-container" :class="{ 'is-active': selectedAbstractType === 'regular', 'is-inactive': selectedAbstractType !== 'regular' }">
            <div class="field-header">
              <h4 class="field-title">
                <i class="fas fa-file-text"></i>
                <span v-if="selectedAbstractType === 'regular'" class="active-indicator">
                  <i class="fas fa-check-circle"></i>
                  פעיל
                </span>
              </h4>
            </div>
            <div class="field-content" @click="selectAbstractType('regular')">
              <RegularAbstract
                v-model="abstract"
                :applicant-id="cv_offer.applicant.resource_id"
                label=""
                :show-not-displayed="true"
                :is-lite-mode="is_lite"
              />
            </div>
          </div>

          <!-- Mixed Abstract Field -->
          <div class="abstract-field-container" :class="{ 'is-active': selectedAbstractType === 'mixed', 'is-inactive': selectedAbstractType !== 'mixed' }">
            <div class="field-header">
              <h4 class="field-title">
                <i class="fas fa-magic"></i>
                <span v-if="selectedAbstractType === 'mixed'" class="active-indicator">
                  <i class="fas fa-check-circle"></i>
                  פעיל
                </span>
              </h4>
            </div>
            <div class="field-content" @click="selectAbstractType('mixed')">
              <MixedAbstract
                v-model="mixed_abstract"
                :applicant-id="cv_offer.applicant.resource_id"
                :offer-id="cv_offer.id"
                label=""
                :key="`mixed-abstract-${cv_offer.id}`"
                @abstractIdChanged="onMixedAbstractIdChanged"
              />
            </div>
          </div>
        </div>
        <div>
          <p><b>title</b></p>
          <input class="outlined" v-model="title" />
        </div>
        <div style="margin-top: 10px;">
          <div class="is-flex is-justify-content-space-between">
            <p><b>greetings</b></p>
            <p><b>ברכות (שלום רב, יקירה, אהובה וכו')</b></p>
          </div>
          <textarea v-model="greetings"></textarea>
        </div>
        <div>
          <div class="is-flex is-justify-content-space-between">
            <p><b>manual_text</b></p>
            <p>
              <b>הערות מקצועיות והתרשמות אישית</b>
              <button @click="fillManualTextFromAI" class="button is-warning is-outlined is-small ml-2" :disabled="is_loading_ai" title="מלא מ-AI">
                <span v-if="is_loading_ai">טוען AI...</span>
                <span v-else>AI</span>
              </button>
              <!-- The row is LTR, so appending puts the cue to the RIGHT of the
                   AI button. Tab order: cue -> textarea -> panel. -->
              <CvReadinessCue
                v-if="cvSuggestionEnabled"
                :cue="cvCueState"
                :panelId="cvPanelId"
                :expanded="cvPanelExpanded"
                @activate="onCueActivate"
              />
            </p>
          </div>
          <!-- Mode-mismatch caution (E-9): reopening in super lite after an
               artifact was applied for a less restrictive mode. -->
          <CvWarnBand v-if="cvCautionMode" :text="cvCautionText" />
          <textarea v-model="manual_text"></textarea>
          <CvSuggestionPanel
            v-if="cvSuggestionEnabled"
            :offerId="cv_offer.id"
            :mode="currentSendMode"
            :typedLength="manualTextTypedLength"
            @use="onSuggestionUse"
            @append="onSuggestionAppend"
            @undo="onSuggestionUndo"
            @regenerate="onSuggestionRegenerate"
          />
          <!-- R-6: standalone confirm for the pre-existing AI button. -->
          <CvSuggestionPanel
            v-if="ai_confirm_open"
            variant="confirm-only"
            :offerId="cv_offer.id"
            :mode="currentSendMode"
            @confirm-accept="onAiButtonConfirmAccept"
            @confirm-cancel="onAiButtonConfirmCancel"
          />
        </div>
        <div>
          <div class="is-flex is-align-items-center mb-2">
            <checkbox v-model="use_email" :checked="use_email">האם לשלוח באימייל?</checkbox>
          </div>
          <p><b>email</b></p>
          <PositionEmailTextArea v-model="recipient" :position="cv_offer.position" />
          <div class="is-flex is-justify-content-space-between" style="text-align: right;">
            <a @click="internal" class="button is-info">Internal</a>
            <checkbox
              :checked="is_set_contact_email"
              v-model="is_set_contact_email"
              >Use this email for this position</checkbox
            >
          </div>
        </div>
        <div style="margin-top: 10px;">
          <div class="is-flex is-align-items-center mb-2">
            <checkbox v-model="use_whatsapp_phones" :checked="use_whatsapp_phones">שלח WhatsApp</checkbox>
          </div>
          <div v-if="use_whatsapp_phones">
            <div style="margin-bottom: 15px; padding: 8px; background-color: #f5f5f5; border-radius: 4px; border-right: 3px solid #3273dc;">
              <div class="is-flex is-justify-content-space-between is-align-items-center mb-2">
                <p style="margin: 0; font-weight: bold; color: #3273dc; font-size: 14px;">ספר טלפונים של החברה:</p>
                <button @click="togglePhoneBookEdit" class="button is-small is-outlined is-info">
                  <span class="icon is-small">
                    <i :class="isEditingPhoneBook ? 'fas fa-save' : 'fas fa-edit'"></i>
                  </span>
                  <span>{{ isEditingPhoneBook ? 'שמור' : 'ערוך' }}</span>
                </button>
              </div>
              <div v-if="!isEditingPhoneBook">
                <p v-if="company_phone_book && company_phone_book.trim().length > 0"
                   style="margin: 0; font-size: 13px; color: #333; white-space: pre-line;">{{ company_phone_book }}</p>
                <p v-else style="margin: 0; font-size: 13px; color: #999; font-style: italic;">אין ספר טלפונים</p>
              </div>
              <div v-else>
                <textarea
                  v-model="company_phone_book"
                  class="textarea is-small"
                  rows="3"
                  placeholder="הזן מספרי טלפון של החברה..."
                  style="font-size: 13px; resize: vertical; min-height: 60px;"></textarea>
              </div>
            </div>
            <p><b>טלפונים לשליחת ווצאפ מופרדים בפסיק</b></p>
            <input
              class="outlined"
              :class="{ 'is-danger': whatsapp_phones_error }"
              v-model="whatsapp_phones"
              placeholder="לדוגמה: 0501234567, 0521234567"
              @blur="validateAndNormalizePhones"
            />
            <div v-if="whatsapp_phones_error" class="has-text-danger is-size-7 mt-1">
              {{ whatsapp_phones_error }}
            </div>
            <div style="font-size: 12px; color: #666; margin-top: 5px;">
              הזן מספרי טלפון מופרדים בפסיק לשליחת הודעות WhatsApp עם קורות החיים
            </div>
          </div>
        </div>
        <div style="text-align: right;">
          <question
            v-if="questions.length"
            @question="setQuestionAnswer"
            :question="
              questions_helper.getQuestionByDesignator(
                questions,
                'files_approved'
              )
            "
          ></question>
          <p><b>files list</b></p>
          <!-- Missing-file band lives INSIDE the files-list label div (issue 8). -->
          <CvWarnBand v-if="cvMissingFileType" :text="cvMissingFileText" />
        </div>
        <Files
          ref="files"
          style="text-align: right;"
          v-if="cv_offer.applicant"
          :payload="{ applicant_data: cv_offer.applicant }"
          :isSendOneFile="isSendOneFile"
          :isSendLite="is_lite"
          :sendMode="'super_lite'"
          :defaultCvType="cvDefaultCvType"
          :key="isLiteRefresh"
          @profileChecked="profileChecked"
          @noDefaultFile="onNoDefaultFile"
        />
      </div>
      <div class="mt-6">
        <div>
          <p><b>התאמה אישית לפרופיל מועמד</b></p>
        </div>
        <div class="columns m-0">
          <div class="is-flex is-flex-direction-column">
            <checkbox v-model="applicant_profile_sections_visibility.abstract"
                      :checked="applicant_profile_sections_visibility.abstract"
                      class="applicant_profile_checkbox">תקציר אישי</checkbox>
            <checkbox v-model="applicant_profile_sections_visibility.manual_text"
                      :checked="applicant_profile_sections_visibility.manual_text"
                      class="applicant_profile_checkbox">מילה מצוות המומחים</checkbox>
            <checkbox v-model="applicant_profile_sections_visibility.skills"
                      :checked="applicant_profile_sections_visibility.skills"
                      class="applicant_profile_checkbox">תחומי עיסוק עיקריים</checkbox>
            <checkbox v-model="applicant_profile_sections_visibility.featured_skills"
                      :checked="applicant_profile_sections_visibility.featured_skills"
                      class="applicant_profile_checkbox">Featured Skills</checkbox>
            <checkbox v-model="applicant_profile_sections_visibility.history_companies"
                      :checked="applicant_profile_sections_visibility.history_companies"
                      class="applicant_profile_checkbox">ניסיון מקצועי</checkbox>
            <checkbox v-model="applicant_profile_sections_visibility.work_experience_achievements"
                      :checked="applicant_profile_sections_visibility.work_experience_achievements"
                      class="applicant_profile_checkbox">הישגים בולטים</checkbox>
            <checkbox v-model="applicant_profile_sections_visibility.academic_education"
                      :checked="applicant_profile_sections_visibility.academic_education"
                      class="applicant_profile_checkbox">השכלה אקדמאית</checkbox>
            <checkbox v-model="applicant_profile_sections_visibility.psychometric"
                      :checked="applicant_profile_sections_visibility.psychometric"
                      class="applicant_profile_checkbox">פסיכומטרי</checkbox>
          </div>
          <div class="is-flex is-flex-direction-column">
            <checkbox v-model="applicant_profile_sections_visibility.additional_activities"
                      :checked="applicant_profile_sections_visibility.additional_activities"
                      class="applicant_profile_checkbox">פעילויות נוספות במסגרת הלימודים</checkbox>
            <checkbox v-model="applicant_profile_sections_visibility.highschool"
                      :checked="applicant_profile_sections_visibility.highschool"
                      class="applicant_profile_checkbox">השכלה תיכונית</checkbox>
            <checkbox v-model="applicant_profile_sections_visibility.military"
                      :checked="applicant_profile_sections_visibility.military"
                      class="applicant_profile_checkbox">שירות צבאי</checkbox>
            <checkbox v-model="applicant_profile_sections_visibility.languages"
                      :checked="applicant_profile_sections_visibility.languages"
                      class="applicant_profile_checkbox">שפות</checkbox>
            <checkbox v-model="applicant_profile_sections_visibility.about_me"
                      :checked="applicant_profile_sections_visibility.about_me"
                      class="applicant_profile_checkbox">קצת עליי</checkbox>
          </div>
        </div>
        <div class="mt-3">
          <a @click="previewApplicantProfile" class="button is-info">Preview Applicant Profile</a>
        </div>
      </div>
      <div class="vertical_space"></div>
      <div class="vertical_space"></div>
      <div class="modal_footer is-justify-content-space-between">
        <div class="is-flex is-align-items-center is-flex-direction-column">
          <div class="is-flex is-align-items-center">
            <!-- Email Send Button -->
            <a
              v-if="use_email"
              @click="preSubmitEmail()"
              :class="{ 'is-loading': is_submitting_email, is_not_submitable: isEmailSubmitDisabled }"
              class="button is-info"
              >שלח אימייל</a
            >
            <div v-if="use_email && use_whatsapp_phones" class="horizontal_space"></div>
            <!-- WhatsApp Send Button -->
            <a
              v-if="use_whatsapp_phones"
              @click="preSubmitWhatsApp()"
              :class="{ 'is-loading': is_submitting_whatsapp, is_not_submitable: isWhatsAppSubmitDisabled }"
              class="button is-success"
              >שלח ווצאפ</a
            >
          </div>
          <div v-if="isSubmitDisabled && submitDisabledReasons.length > 0" class="submit-disabled-reasons mt-2">
            <div v-for="reason in submitDisabledReasons" :key="reason" class="disabled-reason">
              <span class="has-text-danger is-size-7">{{ reason }}</span>
            </div>
          </div>
        </div>
        <div class="is-flex is-align-items-center">
          <a
            @click="openRelatedPositions()"
            class="button is-info"
            >Show Related Positions</a
          >
        </div>
      </div>
    </div>
    <p v-else class="bold">הבקשות נשלחו בהצלחה. אפשר לסגור את החלון.</p>
  </div>
</template>
<script>
import { mapMutations } from 'vuex'
import Validator from '@/custom/classes/validator.js'
import { getOwner } from '@/custom/classes/my_utils.js'
import TimeHelper from '@/custom/classes/time_helper.js'
import TextsHelper from '@/custom/classes/texts_helper.js'
import OffersHelper from '@/custom/classes/offers_helper.js'
import QuestionsHelper from '@/custom/classes/questions_helper.js'
import NetworkUtils from '@/custom/classes/network_utils.js'
import Checkbox from '@/components/Checkbox'
import Question from '@/components/Question'
import Files from '@/components/Files'
import StartVetting from '@/components/applicants/StartVetting.vue'
import PositionEmailTextArea from '@/components/PositionEmailTextArea.vue'
import CvOfferAlerts from '@/components/CvOfferAlerts.vue'
import Gender from '@/components/entities/applicant/Gender'
import RegularAbstract from '@/components/entities/applicant/RegularAbstract.vue'
import MixedAbstract from '@/components/entities/applicant/MixedAbstract.vue'
import FileHelper from '@/custom/classes/file_helper.js'
import { MISSING_OFFER_MESSAGE, hasSelectedOffer } from '@/custom/classes/cv_send_guards.js'
import { BLOCKED_TOAST, isApplicantBlocked } from '@/custom/helpers/applicant_block.js'
import CvReadinessCue from '@/components/cv/CvReadinessCue.vue'
import CvSuggestionPanel from '@/components/cv/CvSuggestionPanel.vue'
import CvWarnBand from '@/components/cv/CvWarnBand.vue'
import { MODE_LABELS, CONFIRM_MIN_CHARS } from '@/store/modules/cvTextArtifacts'
export default {
  name: 'cv-modal-super-lite',
  components: {
    Checkbox,
    Question,
    Files,
    StartVetting,
    PositionEmailTextArea,
    CvOfferAlerts,
    Gender,
    RegularAbstract,
    MixedAbstract,
    CvReadinessCue,
    CvSuggestionPanel,
    CvWarnBand
  },
  props: {
    payload: {
      type: Object,
      required: true
    },
    updateTitle: {
      type: Function,
      default: () => {}
    }
  },
  data () {
    return {
      is_vip: false,
      is_lite: true,
      is_super_hide: false,
      currentSendMode: 'super_lite', // For the new radio selector
      isLiteRefresh: 1,
      is_submitted: false,
      validator: new Validator(),
      time_helper: new TimeHelper(),
      texts_helper: new TextsHelper(),
      offers_helper: new OffersHelper(),
      questions_helper: new QuestionsHelper(),
      network_utils: new NetworkUtils(),
      cv_offer: this.payload.data,
      title: '',
      recipient: '',
      applicantGender: '',
      is_loading_data: false,
      is_loading_ai: false,
      is_submitting_cv: false,
      is_submitting_email: false,
      is_submitting_whatsapp: false,
      is_set_contact_email: true,
      selectedAbstractType: 'regular', // 'regular' or 'mixed'
      abstract: '',
      mixed_abstract: '',
      mixed_abstract_id: null,
      questions: [],
      questions_answers: {},
      manual_text: '',
      company_position_id_content: '',
      greetings: 'שלום רב,',
      whatsapp_phones: '',
      whatsapp_phones_error: '',
      use_whatsapp_phones: false,
      use_email: true,
      is_submitable: false,
      isNotValid: false,
      isSendOneFile: undefined,
      redAlertOffer: {},
      company_phone_book: '',
      isEditingPhoneBook: false,
      isEditingAbstract: false,
      stat_id: null,
      file_helper: new FileHelper(),
      cv_missing_file_type: null,
      ai_confirm_open: false,
      applicant_profile_sections_visibility: {
        abstract: true,
        manual_text: true,
        skills: true,
        featured_skills: false,
        history_companies: false,
        work_experience_achievements: false,
        academic_education: false,
        psychometric: false,
        additional_activities: false,
        highschool: false,
        military: false,
        languages: false,
        about_me: false
      }
    }
  },
  computed: {
    submitButtonText () {
      if (this.use_email && this.use_whatsapp_phones) {
        return 'שלח אימייל ווצאפ'
      } else if (this.use_email) {
        return 'שלח אימייל'
      } else if (this.use_whatsapp_phones) {
        return 'שלח ווצאפ'
      } else {
        return 'Submit'
      }
    },
    // An offer must be loaded before anything can be sent: without it the
    // request goes out with offer_id undefined and the server answers a
    // generic 500 while the email is silently never sent.
    hasSelectedOffer () {
      return hasSelectedOffer(this.cv_offer)
    },
    // Blocked candidate (v2): hard stop, not an acknowledgeable warning - no override here.
    applicantBlocked () {
      return isApplicantBlocked(this.cv_offer && this.cv_offer.applicant)
    },
    missingOfferReasons () {
      const reasons = this.hasSelectedOffer ? [] : [MISSING_OFFER_MESSAGE]
      return this.applicantBlocked ? [BLOCKED_TOAST, ...reasons] : reasons
    },
    submitDisabledReasons () {
      const reasons = [...this.missingOfferReasons]

      // Check if gender is not selected
      if (!this.applicantGender || this.applicantGender === '') {
        reasons.push('יש לבחור מין למועמד')
      }

      // Check if files approval question is not answered (for non-lite mode)
      if (!this.is_lite && (!this.questions_answers.files_approved || this.questions_answers.files_approved === undefined)) {
        reasons.push('יש לענות על שאלת אישור הקבצים')
      }

      // Check if files are not approved properly (for non-lite mode)
      if (!this.is_lite && this.questions_answers.files_approved) {
        const is_files_approved_true = this.questions_helper.getDisplayTextForSingleOption(
          this.questions_answers.files_approved.answer,
          this.questions_answers.files_approved.question
        ) === 'TRUE'
        const is_files_approved_checked_one_option = this.questions_answers.files_approved.answer.split(',').length === 1

        if (!is_files_approved_true) {
          reasons.push('יש לאשר את הקבצים')
        } else if (!is_files_approved_checked_one_option) {
          reasons.push('יש לבחור אפשרות אחת בלבד לאישור קבצים')
        }
      }

      // Check if warnings/alerts are not handled
      if (this.isNotValid) {
        reasons.push('יש לסמן את כל האזהרות והודעות')
      }

      // Check WhatsApp phone validation if WhatsApp is enabled
      if (this.use_whatsapp_phones && this.whatsapp_phones_error) {
        reasons.push('יש לתקן את מספרי הטלפון לווצאפ')
      }

      // Check if neither email nor WhatsApp is selected
      if (!this.use_email && !this.use_whatsapp_phones) {
        reasons.push('יש לבחור לפחות דרך אחת לשליחה: אימייל או ווצאפ')
      }

      return reasons
    },
    isSubmitDisabled () {
      return (!this.is_submitable || this.isNotValid || this.applicantBlocked || this.submitDisabledReasons.length > 0)
    },
    isEmailSubmitDisabled () {
      const reasons = [...this.missingOfferReasons]

      // Check if gender is not selected
      if (!this.applicantGender || this.applicantGender === '') {
        reasons.push('יש לבחור מין למועמד')
      }

      // Check if warnings/alerts are not handled
      if (this.isNotValid) {
        reasons.push('יש לסמן את כל האזהרות והודעות')
      }

      return (!this.is_submitable || this.isNotValid || this.applicantBlocked || reasons.length > 0)
    },
    isWhatsAppSubmitDisabled () {
      const reasons = [...this.missingOfferReasons]

      // Check if gender is not selected
      if (!this.applicantGender || this.applicantGender === '') {
        reasons.push('יש לבחור מין למועמד')
      }

      // Check if warnings/alerts are not handled
      if (this.isNotValid) {
        reasons.push('יש לסמן את כל האזהרות והודעות')
      }

      // Check WhatsApp phone validation
      if (this.whatsapp_phones_error) {
        reasons.push('יש לתקן את מספרי הטלפון לווצאפ')
      }

      return (!this.is_submitable || this.isNotValid || this.applicantBlocked || reasons.length > 0)
    },

    // ---- CV suggestion feature (flag-gated; inert when off) ----
    cvOfferId () {
      return this.cv_offer && this.cv_offer.id
    },
    cvUiEnabled () {
      return this.$store.getters['cvTextArtifacts/uiEnabled'] === true
    },
    cvSuggestionEnabled () {
      return this.cvUiEnabled && this.$store.getters['cvTextArtifacts/aiTextsEnabled'] === true
    },
    cvPanelId () {
      return `sg-panel-${this.cvOfferId}`
    },
    cvPanel () {
      return this.$store.getters['cvTextArtifacts/panelFor'](this.cvOfferId)
    },
    cvPanelExpanded () {
      return this.cvPanel.expanded === true
    },
    cvPanelView () {
      return this.$store.getters['cvTextArtifacts/panelView'](this.cvOfferId, this.currentSendMode, Date.now())
    },
    cvCueState () {
      if (!this.cvSuggestionEnabled) return 'absent'
      return this.$store.getters['cvTextArtifacts/cueState'](this.cvOfferId, this.currentSendMode, Date.now())
    },
    cvTelemetry () {
      return this.cvPanel.telemetry || { suggestion_applied: false, suggestion_source_mode: null, suggestion_text: '' }
    },
    manualTextTypedLength () {
      return (this.manual_text || '').trim().length
    },
    cvCautionMode () {
      if (!this.cvSuggestionEnabled) return null
      return this.$store.getters['cvTextArtifacts/cautionModeFor'](this.cvOfferId, this.currentSendMode)
    },
    cvCautionText () {
      const label = MODE_LABELS[this.cvCautionMode] || this.cvCautionMode
      return `הטקסט בשדה ההערות נוצר עבור שליחה ${label} ועלול לכלול שם מלא או מקום עבודה — עברי עליו לפני השליחה.`
    },
    cvDefaultCvType () {
      if (!this.cvUiEnabled) return ''
      return this.file_helper.getCvTypeForMode(this.currentSendMode)
    },
    cvMissingFileType () {
      return this.cv_missing_file_type
    },
    cvMissingFileText () {
      const label = MODE_LABELS[this.currentSendMode] || this.currentSendMode
      return `לא נמצא קובץ קורות חיים מתאים לשליחת ${label} — בחרי קובץ מהרשימה.`
    }
  },
  watch: {
    // Telemetry-clear watcher (UX §7.6 / R-4) + dirty-close guard registration.
    manual_text (newVal) {
      this.onManualTextChanged(newVal)
      this.syncDirtyGuard(newVal)
    },
    is_lite (newVal) {
      if (newVal) {
        this.updateTitle(`${this.payload.modal_title} (Super Lite)`)
      } else {
        this.updateTitle(this.payload.modal_title.replace(/\s*Super\s*Lite\s*/i, ' ').trim())
      }
      this.title = this.getTitle()
    },
    whatsapp_phones (newVal) {
      // Update position's contact_phones when whatsapp_phones changes
      this.updatePositionContactPhones(newVal)
    }
  },
  async created () {
    await this.createStat()
    this.init()
  },
  beforeDestroy () {
    this.clearDirtyGuard(this.payload.modal_id)
    if (this.cvOfferId) {
      this.$store.dispatch('cvTextArtifacts/closeSession', this.cvOfferId)
    }
  },
  methods: {
    async createStat () {
      try {
        // Step 1: Check for existing stat within last 3 months
        const threeMonthsAgo = new Date()
        threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3)

        const existingStatsResponse = await this.network_utils.getStats({
          resource_type: 'Offer',
          resource_id: this.cv_offer.id,
          designator: 'send_cv_modal_opened',
          date_from: threeMonthsAgo.toISOString()
        })

        // Step 2: If recent stat exists, use the most recent one
        if (existingStatsResponse && existingStatsResponse.length > 0) {
          // Sort by created_at descending to get the most recent one
          const sortedStats = existingStatsResponse.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          this.stat_id = sortedStats[0].id
          return
        }

        // Step 3: If no recent stat, create new one
        const statData = {
          resource_type: 'Offer',
          resource_id: this.cv_offer.id,
          designator: 'send_cv_modal_opened',
          owner: getOwner().name,
          title: 'CV Modal Opened',
          content: 'Super lite CV modal opened for offer',
          status: 'active',
          created_by: 'Admin'
        }

        const response = await this.network_utils.postStat(statData)
        this.stat_id = response.id
      } catch (error) {
        console.error('Failed to create/find stat:', error)
        // Continue without stat_id if operation fails
      }
    },
    warningsStatus (status) {
      this.isNotValid = !status
    },
    setRedAlert (offer) {
      this.redAlertOffer = offer
    },
    setIsSubmitable () {
      if (
        this.applicantGender == undefined ||
        (this.questions_answers.files_approved == undefined)
      ) {
        this.is_submitable = false
        return
      }
      let is_gender_checked = this.applicantGender != ''
      let is_files_approved_true =
        this.questions_helper.getDisplayTextForSingleOption(
          this.questions_answers.files_approved.answer,
          this.questions_answers.files_approved.question
        ) == 'TRUE'
      let is_files_approved_checked_one_option =
        this.questions_answers.files_approved.answer.split(',').length == 1
      let is_files_approved = is_files_approved_true && is_files_approved_checked_one_option
      this.is_submitable = is_gender_checked && (this.is_lite || is_files_approved)
      this.isLiteRefresh++
    },
    internal () {
      this.recipient = this.$settings.get({ path: 'operation_email' })
      this.is_set_contact_email = false
    },
    profileChecked (v) {
      if (!v) {
        Object.entries(this.applicant_profile_sections_visibility).forEach(([key, value]) => {
          this.applicant_profile_sections_visibility[key] = false
        })
      }
    },
    // Last line of defence: the disabled styling is CSS-only, and the
    // confirmation dialog re-enters submit* through a callback, so the guard
    // has to sit on the send path itself and not only on the button.
    guardSelectedOffer () {
      if (this.applicantBlocked) {
        this.$utils.emit('show_toast', { text: BLOCKED_TOAST })
        return false
      }
      if (this.hasSelectedOffer) {
        return true
      }
      this.$utils.emit('show_toast', { text: MISSING_OFFER_MESSAGE })
      return false
    },
    preSubmit () {
      if (!this.guardSelectedOffer()) {
        return
      }
      if (this.redAlertOffer || this.payload.data.position.company.priority_warning) {
        this.create({
          designator: 'cv-send-conformation',
          component_name: 'CvSendConfirm',
          modal_title: 'אישור שליחת קורות חיים',
          show_close_button: false,
          addition_warnings: [this.payload.data.position.company.priority_warning],
          offer: this.redAlertOffer,
          resolve: () => {
            this.submit()
          },
          reject: () => {

          }
        })
      } else {
        this.submit()
      }
    },
    preSubmitEmail () {
      if (!this.guardSelectedOffer()) {
        return
      }
      if (this.redAlertOffer || this.payload.data.position.company.priority_warning) {
        this.create({
          designator: 'cv-send-conformation',
          component_name: 'CvSendConfirm',
          modal_title: 'אישור שליחת קורות חיים באימייל',
          show_close_button: false,
          addition_warnings: [this.payload.data.position.company.priority_warning],
          offer: this.redAlertOffer,
          resolve: () => {
            this.submitEmail()
          },
          reject: () => {

          }
        })
      } else {
        this.submitEmail()
      }
    },
    preSubmitWhatsApp () {
      if (!this.guardSelectedOffer()) {
        return
      }
      if (this.redAlertOffer || this.payload.data.position.company.priority_warning) {
        this.create({
          designator: 'cv-send-conformation',
          component_name: 'CvSendConfirm',
          modal_title: 'אישור שליחת קורות חיים בווצאפ',
          show_close_button: false,
          addition_warnings: [this.payload.data.position.company.priority_warning],
          offer: this.redAlertOffer,
          resolve: () => {
            this.submitWhatsApp()
          },
          reject: () => {

          }
        })
      } else {
        this.submitWhatsApp()
      }
    },
    submit () {
      if (!this.guardSelectedOffer()) {
        return
      }
      let greetings = this.texts_helper.getBoldContent(
        this.greetings,
        'greetings'
      )
      let manual_text = this.texts_helper.getBoldContent(
        this.manual_text,
        'manual_text'
      )
      if (greetings == 'problem' || manual_text == 'problem') {
        return
      }
      if (this.is_lite) {
        // Get the selected abstract content
        let selectedAbstract = this.selectedAbstractType === 'mixed' ? this.mixed_abstract : this.abstract
        let lite_abstract = this.texts_helper.getBoldContent(selectedAbstract, 'abstract')

        if (lite_abstract == 'problem') {
          return
        }
        if (lite_abstract.length == 0) {
          this.$utils.emit('show_toast', { text: 'אבסטרקט ריק' })
          return
        }
        if (!confirm('את/ה עומד/ת לשלוח קו"ח במצב SUPER LITE.\nהאם בטוח לשלוח?')) {
          return
        }
      }
      if (!this.validator.validateMultipleEmails({ value: this.recipient })) {
        return
      }
      this.is_submitting_cv = true
      let requests = [...this.answerQuestions(), ...this.answerTexts(), this.answerUser()]
      this.$caller.axios.all(requests).then(() => {
        this.$caller.axios.all([...this.sendEvents(), this.answerOffer()]).then(() => {
          this.$network_utils.getResourceOffer({ offer_id: this.cv_offer.id }).then(response => {
            if (response.status.name == 'awaiting_company_sent') {
              this.$network_utils.updateApplicant({ applicant_id: this.cv_offer.applicant.resource_id, active_at: new Date() })
              this.is_submitting_cv = false
              this.showAwaitingCompanySentCount()
              this.openRelatedPositions()
              this.is_submitted = true
              this.remove(this.payload)
              return
            }
            this.showSubmitStatusError()
          })
        }).catch(() => {
          this.showSubmitErrorMessage()
        })
      }).catch(() => {
        this.showSubmitErrorMessage()
      })
    },
    submitEmail () {
      if (!this.guardSelectedOffer()) {
        return
      }
      let greetings = this.texts_helper.getBoldContent(
        this.greetings,
        'greetings'
      )
      let manual_text = this.texts_helper.getBoldContent(
        this.manual_text,
        'manual_text'
      )
      if (greetings == 'problem' || manual_text == 'problem') {
        return
      }
      if (this.is_lite) {
        // Get the selected abstract content
        let selectedAbstract = this.selectedAbstractType === 'mixed' ? this.mixed_abstract : this.abstract
        let lite_abstract = this.texts_helper.getBoldContent(selectedAbstract, 'abstract')

        if (lite_abstract == 'problem') {
          return
        }
        if (lite_abstract.length == 0) {
          this.$utils.emit('show_toast', { text: 'אבסטרקט ריק' })
          return
        }
        if (!confirm('את/ה עומד/ת לשלוח קו"ח באימייל במצב SUPER LITE.\nהאם בטוח לשלוח?')) {
          return
        }
      }
      if (!this.validator.validateMultipleEmails({ value: this.recipient })) {
        return
      }
      this.is_submitting_email = true
      let requests = [...this.answerQuestions(), ...this.answerTexts(), this.answerUser()]
      this.$caller.axios.all(requests).then(() => {
        this.$caller.axios.all([...this.sendEmailEvents(), this.answerOffer()]).then(() => {
          this.$network_utils.getResourceOffer({ offer_id: this.cv_offer.id }).then(response => {
            if (response.status.name == 'awaiting_company_sent') {
              this.$network_utils.updateApplicant({ applicant_id: this.cv_offer.applicant.resource_id, active_at: new Date() })
              this.is_submitting_email = false
              this.showAwaitingCompanySentCount()
              this.openRelatedPositions()
              this.is_submitted = true
              this.remove(this.payload)
              return
            }
            this.showSubmitStatusError()
          })
        }).catch(() => {
          this.showEmailSubmitErrorMessage()
        })
      }).catch(() => {
        this.showEmailSubmitErrorMessage()
      })
    },
    submitWhatsApp () {
      if (!this.guardSelectedOffer()) {
        return
      }
      let greetings = this.texts_helper.getBoldContent(
        this.greetings,
        'greetings'
      )
      let manual_text = this.texts_helper.getBoldContent(
        this.manual_text,
        'manual_text'
      )
      if (greetings == 'problem' || manual_text == 'problem') {
        return
      }
      if (this.is_lite) {
        // Get the selected abstract content
        let selectedAbstract = this.selectedAbstractType === 'mixed' ? this.mixed_abstract : this.abstract
        let lite_abstract = this.texts_helper.getBoldContent(selectedAbstract, 'abstract')

        if (lite_abstract == 'problem') {
          return
        }
        if (lite_abstract.length == 0) {
          this.$utils.emit('show_toast', { text: 'אבסטרקט ריק' })
          return
        }
        if (!confirm('את/ה עומד/ת לשלוח קו"ח בווצאפ במצב SUPER LITE.\nהאם בטוח לשלוח?')) {
          return
        }
      }
      this.is_submitting_whatsapp = true
      let requests = [...this.answerQuestions(), ...this.answerTexts(), this.answerUser()]
      this.$caller.axios.all(requests).then(() => {
        this.$caller.axios.all([...this.sendWhatsAppEvents(), this.answerOffer()]).then(() => {
          this.$network_utils.getResourceOffer({ offer_id: this.cv_offer.id }).then(response => {
            if (response.status.name == 'awaiting_company_sent') {
              this.$network_utils.updateApplicant({ applicant_id: this.cv_offer.applicant.resource_id, active_at: new Date() })
              this.is_submitting_whatsapp = false
              this.showAwaitingCompanySentCount()
              this.openRelatedPositions()
              this.is_submitted = true
              this.remove(this.payload)
              return
            }
            this.showSubmitStatusError()
          })
        }).catch(() => {
          this.showWhatsAppSubmitErrorMessage()
        })
      }).catch(() => {
        this.showWhatsAppSubmitErrorMessage()
      })
    },
    showSubmitErrorMessage () {
      this.$utils.emit('show_toast', { text: 'ארעה שגיאה' })
      this.is_submitting_cv = false
    },
    showEmailSubmitErrorMessage () {
      this.$utils.emit('show_toast', { text: 'ארעה שגיאה בשליחת האימייל' })
      this.is_submitting_email = false
    },
    showWhatsAppSubmitErrorMessage () {
      this.$utils.emit('show_toast', { text: 'ארעה שגיאה בשליחת הווצאפ' })
      this.is_submitting_whatsapp = false
    },
    showSubmitStatusError () {
      this.create({
        designator: 'offer-status-not-changed-error-modal',
        component_name: 'CVOfferStatusErrorForm',
        offer: this.cv_offer,
        modal_title: 'ארעה שגיאה בשינוי סטטוס'
      })
      this.$utils.emit('show_toast', { text: 'שינוי סטטוס לא הצליח - אנא פנה/י לעמית' })
    },
    showAwaitingCompanySentCount () {
      this.$network_utils.getResourceOffers({
        resource_type: 'Position',
        company_id: this.cv_offer.position.company.resource_id,
        resource_id: this.cv_offer.position.id,
        statuses: 'awaiting_company_sent'
      }).then(response => {
        if (response == undefined) {
          return
        }
        this.$utils.emit('show_toast', { text: 'awaiting_company_sent ' + response.length })
      })
    },
    openRelatedPositions () {
      this.create({
        designator: 'related-positions-modal',
        component_name: 'RelatedPositions',
        position: this.cv_offer.position,
        applicant: this.cv_offer.applicant,
        modal_title: this.cv_offer.position.title + ' - ' + this.cv_offer.position.company.name + ' - Related Positions '
      })
    },
    answerQuestions () {
      let requests = []
      for (let designator in this.questions_answers) {
        let question = this.questions_answers[designator]
        requests.push(this.$caller.request(
          'post',
          '/questions/' + question.question.id + '/answers/',
          'shared_headers',
          {
            current_type: 'Applicant',
            current_resource_id: this.cv_offer.applicant.resource_id,
            option_ids: question.answer
          }
        ))
      }
      return requests
    },
    sendEvents () {
      let greetings = this.texts_helper.getBoldContent(
        this.greetings,
        'greetings'
      )
      let manual_text = this.texts_helper.getBoldContent(
        this.manual_text,
        'manual_text'
      )
      let events = []

      // Add email event only if email checkbox is checked
      if (this.use_email) {
        let data = {
          current_type: 'Applicant',
          current_resource_id: this.cv_offer.applicant.resource_id,
          applicant_id: this.cv_offer.applicant.resource_id,
          recipient: this.recipient,
          offer_id: this.cv_offer.id,
          event: this.is_lite ? 'send_cv_lite_email' : 'send_cv_email',
          owner: getOwner().name,
          greetings: greetings,
          title: this.title,
          is_vip: this.is_vip,
          is_super_hide: this.is_super_hide,
          applicant_profile_sections_visibility: this.applicant_profile_sections_visibility
        }
        if (this.manual_text.length > 0) {
          data.manual_text = manual_text
        }
        data.files_list = this.$refs.files.getCheckedFileNames().join(',')
        if (this.is_lite) {
          // Use the selected abstract type
          if (this.selectedAbstractType === 'mixed') {
            data.abstract = this.mixed_abstract
            // Include applicant_abstract_id when using mixed abstract
            if (this.mixed_abstract_id) {
              data.applicant_abstract_id = this.mixed_abstract_id
            }
          } else {
            data.abstract = this.abstract
          }
        }
        Object.assign(data, this.cvEventParams())
        events.push(this.$network_utils.postEvent(data))
      }

      // Add WhatsApp events for each phone number only if checkbox is checked
      if (this.use_whatsapp_phones && this.whatsapp_phones && this.whatsapp_phones.trim().length > 0) {
        const phones = this.whatsapp_phones.split(',').map(phone => phone.trim()).filter(phone => phone.length > 0)

        phones.forEach(phone => {
          const whatsappData = {
            current_type: 'Applicant',
            current_resource_id: this.cv_offer.applicant.resource_id,
            event: 'send_cv_whatsapp',
            applicant_id: this.cv_offer.applicant.resource_id,
            offer_id: this.cv_offer.id,
            phone_number: phone,
            owner: getOwner().name,
            greetings: greetings,
            manual_text: manual_text || '',
            title: this.title,
            abstract: this.is_lite ? (this.selectedAbstractType === 'mixed' ? this.mixed_abstract : this.abstract) : this.abstract,
            files_list: this.$refs.files.getCheckedFileNames().join(','),
            is_super_hide: this.is_super_hide,
            super_lite: true
          }
          Object.assign(whatsappData, this.cvEventParams())
          events.push(this.$network_utils.postEvent(whatsappData))
        })
      }

      return events
    },
    sendEmailEvents () {
      let greetings = this.texts_helper.getBoldContent(
        this.greetings,
        'greetings'
      )
      let manual_text = this.texts_helper.getBoldContent(
        this.manual_text,
        'manual_text'
      )
      let events = []

      // Add only email event
      let data = {
        current_type: 'Applicant',
        current_resource_id: this.cv_offer.applicant.resource_id,
        applicant_id: this.cv_offer.applicant.resource_id,
        recipient: this.recipient,
        offer_id: this.cv_offer.id,
        event: this.is_lite ? 'send_cv_lite_email' : 'send_cv_email',
        owner: getOwner().name,
        greetings: greetings,
        title: this.title,
        is_vip: this.is_vip,
        is_super_hide: this.is_super_hide,
        applicant_profile_sections_visibility: this.applicant_profile_sections_visibility
      }
      if (this.manual_text.length > 0) {
        data.manual_text = manual_text
      }
      data.files_list = this.$refs.files.getCheckedFileNames().join(',')
      if (this.is_lite) {
        // Use the selected abstract type
        if (this.selectedAbstractType === 'mixed') {
          data.abstract = this.mixed_abstract
          // Include applicant_abstract_id when using mixed abstract
          if (this.mixed_abstract_id) {
            data.applicant_abstract_id = this.mixed_abstract_id
          }
        } else {
          data.abstract = this.abstract
        }
      }
      Object.assign(data, this.cvEventParams())
      events.push(this.$network_utils.postEvent(data))

      return events
    },
    sendWhatsAppEvents () {
      let greetings = this.texts_helper.getBoldContent(
        this.greetings,
        'greetings'
      )
      let manual_text = this.texts_helper.getBoldContent(
        this.manual_text,
        'manual_text'
      )
      let events = []

      // Add WhatsApp events for each phone number
      if (this.whatsapp_phones && this.whatsapp_phones.trim().length > 0) {
        const phones = this.whatsapp_phones.split(',').map(phone => phone.trim()).filter(phone => phone.length > 0)

        phones.forEach(phone => {
          const whatsappData = {
            current_type: 'Applicant',
            current_resource_id: this.cv_offer.applicant.resource_id,
            event: 'send_cv_whatsapp',
            applicant_id: this.cv_offer.applicant.resource_id,
            offer_id: this.cv_offer.id,
            phone_number: phone,
            owner: getOwner().name,
            greetings: greetings,
            manual_text: manual_text || '',
            title: this.title,
            abstract: this.is_lite ? (this.selectedAbstractType === 'mixed' ? this.mixed_abstract : this.abstract) : this.abstract,
            files_list: this.$refs.files.getCheckedFileNames().join(','),
            is_super_hide: this.is_super_hide,
            super_lite: true
          }
          Object.assign(whatsappData, this.cvEventParams())
          events.push(this.$network_utils.postEvent(whatsappData))
        })
      }

      return events
    },
    answerOffer () {
      return this.offers_helper.changeOfferStatus({
        offer: this.cv_offer,
        new_data: { status: 'awaiting_company_sent', owner: getOwner().name, sent_at: new Date().toUTCString() }
      })
    },
    answerTexts () {
      let requests = []
      if (this.is_set_contact_email) {
        requests.push(this.$caller.request('post', '/resource_texts/', 'shared_headers', {
          current_type: 'Position',
          current_resource_id: this.cv_offer.position.id,
          designator: 'contact_email',
          content: this.recipient
        }))
      }
      if (this.is_lite) {
      } else {
        requests.push(this.$caller.request('post', '/resource_texts/', 'shared_headers', {
          current_type: 'Applicant',
          current_resource_id: this.cv_offer.applicant.resource_id,
          designator: 'approved_files',
          content: this.$refs.files.getCheckedFileNames().join(',')
        }))
        requests.push(this.$caller.request('post', '/resource_texts/', 'shared_headers', {
          current_type: 'Applicant',
          current_resource_id: this.cv_offer.applicant.resource_id,
          designator: 'rejected_files',
          content: this.$refs.files.getRejectedFileNames().join(',')
        }))
      }
      return requests
    },
    answerUser () {
      let requests = []
      requests.push(this.$caller.request(
        'put',
        '/users/' + this.cv_offer.applicant.id,
        'admin_headers',
        {
          id: this.cv_offer.applicant.id
        }
      ))
      return requests
    },
    init () {
      this.is_loading_data = true

      // Add safety checks to prevent undefined errors
      if (!this.cv_offer || !this.cv_offer.position || !this.cv_offer.position.company || !this.cv_offer.applicant) {
        console.error('CVModalSuperLiteForm: Missing required data in cv_offer', this.cv_offer)
        this.$utils.emit('show_toast', { text: 'שגיאה: נתונים חסרים בהצעה' })
        this.is_loading_data = false
        return
      }

      let company_questions_request = this.$network_utils.getResourceQuestions({
        current_type: 'Company',
        current_resource_id: this.cv_offer.position.company.id,
        designators: 'is_send_one_file,has_contract'
      })
      let questions_request = this.$network_utils.getResourceQuestions({
        current_type: 'Applicant',
        current_resource_id: this.cv_offer.applicant.resource_id,
        designators: 'files_approved'
      })
      // Add request to get fresh company data
      let company_data_request = this.$network_utils.getCompany({
        id: this.cv_offer.position.company.resource_id
      })

      let requests = [
        questions_request,
        company_questions_request,
        company_data_request
      ]
      this.$caller.axios.all(requests).then(response => {
        this.questions = response[0]
        const company_is_send_one_file = this.questions_helper.getQuestionByDesignator(response[1], 'is_send_one_file')
        const company_has_contract = this.questions_helper.getQuestionByDesignator(response[1], 'has_contract')
        const fresh_company_data = response[2]

        this.isSendOneFile = this.questions_helper.getSingleAnswerText(company_is_send_one_file) == 'TRUE'
        this.cv_offer = { ...this.cv_offer, isContractSign: this.questions_helper.getSingleAnswerText(company_has_contract) == 'Signed' }

        // Update company data with fresh data from server
        this.cv_offer.position.company = { ...this.cv_offer.position.company, ...fresh_company_data }

        this.abstract = this.cv_offer.applicant.abstract || ''
        this.lite_abstract = this.abstract || ''
        this.applicantGender = this.cv_offer.applicant.gender || undefined
        this.company_position_id_content = this.cv_offer.position.company_position_id
        this.title = this.getTitle()

        // Load contact phones from position contact_phones
        if (this.cv_offer.position.contact_phones && this.cv_offer.position.contact_phones.trim().length > 0) {
          this.whatsapp_phones = this.cv_offer.position.contact_phones
          this.use_whatsapp_phones = true
        } else if (this.cv_offer.position.company.phone_book && this.cv_offer.position.company.phone_book.trim().length > 0) {
          // Fallback to company phone book if position contact_phones is empty
          this.whatsapp_phones = this.cv_offer.position.company.phone_book
          this.use_whatsapp_phones = true
        }

        // Initialize company phone book with fresh data
        this.company_phone_book = this.cv_offer.position.company.phone_book || ''

        this.is_loading_data = false
        this.cvInit()
      })
    },
    fillManualTextFromAI () {
      // R-6: the old AI button gains the same >=20-typed-character confirm.
      if (this.cvSuggestionEnabled && !this.ai_confirm_open && this.manualTextTypedLength >= CONFIRM_MIN_CHARS) {
        this.ai_confirm_open = true
        return
      }
      this.ai_confirm_open = false
      const aiEval = this.$store.getters['aiEvaluation/getEvaluation'](this.cv_offer.id)
      if (aiEval && aiEval.proposer && aiEval.proposer.reasoning) {
        // WRITE SITE 1 (cached path).
        this.beforeForeignProgrammaticWrite(aiEval.proposer.reasoning)
        this.manual_text = aiEval.proposer.reasoning
        return
      }
      this.is_loading_ai = true
      this.$store.dispatch('aiEvaluation/fetchEvaluation', {
        offerId: this.cv_offer.id, force: false, vm: this
      }).then(result => {
        if (result && result.proposer && result.proposer.reasoning) {
          // WRITE SITE 2 (async fetch path). Both sites gated — §12.1.
          this.beforeForeignProgrammaticWrite(result.proposer.reasoning)
          this.manual_text = result.proposer.reasoning
        }
      }).finally(() => { this.is_loading_ai = false })
    },
    onAiButtonConfirmAccept () {
      this.ai_confirm_open = false
      this.fillManualTextFromAI()
    },
    onAiButtonConfirmCancel () {
      this.ai_confirm_open = false
    },
    // §12.1: a programmatic write from a DIFFERENT affordance is a transfer of
    // ownership. Clear the suggestion telemetry and drop the undo snapshot
    // BEFORE writing, so `בטלי` cannot destroy the proposer text.
    beforeForeignProgrammaticWrite (value) {
      if (!this.cvOfferId) return
      this.$store.dispatch('cvTextArtifacts/clearSuggestionTelemetry', this.cvOfferId)
      this.$store.dispatch('cvTextArtifacts/setLastProgrammaticValue', { offerId: this.cvOfferId, value: value })
    },

    // ---- suggestion panel wiring ------------------------------------------
    cvInit () {
      if (!this.cvOfferId) return
      this.$store.commit('cvTextArtifacts/initPanel', this.cvOfferId)
      this.$store.dispatch('cvTextArtifacts/fetchFlags', { vm: this }).then(() => {
        if (!this.cvSuggestionEnabled) return
        const existing = this.$store.getters['cvTextArtifacts/envelopeFor'](this.cvOfferId, this.currentSendMode)
        if (!existing) {
          this.$store.dispatch('cvTextArtifacts/generate', {
            offerId: this.cvOfferId, mode: this.currentSendMode, vm: this
          })
          return
        }
        if (existing.terminal !== true) {
          this.$store.dispatch('cvTextArtifacts/startPolling', {
            offerId: this.cvOfferId, mode: this.currentSendMode, vm: this
          })
        }
      })
    },
    // C4.2: this form is fixed-mode, so changing mode means closing it. The X is
    // the ONLY close path in Modal.vue (no ESC, no backdrop), which is why the
    // guard is a store flag that closeModal() consults.
    syncDirtyGuard (value) {
      this.setDirtyGuard({
        modal_id: this.payload.modal_id,
        value: !!(value && value.trim().length > 0)
      })
    },
    onCueActivate () {
      this.$store.dispatch('cvTextArtifacts/setExpanded', { offerId: this.cvOfferId, value: true })
    },
    onNoDefaultFile (cvType) {
      this.cv_missing_file_type = cvType
    },
    onSuggestionUse () {
      const view = this.cvPanelView
      if (!view.suggestedText) return
      const previousText = this.manual_text
      const previousFlags = { ...this.cvTelemetry }
      this.$store.dispatch('cvTextArtifacts/setLastProgrammaticValue', {
        offerId: this.cvOfferId, value: view.suggestedText
      })
      this.manual_text = view.suggestedText
      this.$store.dispatch('cvTextArtifacts/recordApplied', {
        offerId: this.cvOfferId,
        appliedText: view.suggestedText,
        appliedMode: view.reusedFromMode || this.currentSendMode,
        appliedKind: 'use',
        previousText: previousText,
        previousFlags: previousFlags
      })
      this.$store.dispatch('cvTextArtifacts/setExpanded', { offerId: this.cvOfferId, value: false })
    },
    onSuggestionAppend () {
      const view = this.cvPanelView
      if (!view.suggestedText) return
      const previousText = this.manual_text
      const previousFlags = { ...this.cvTelemetry }
      const joined = previousText && previousText.length > 0
        ? `${previousText}\n\n${view.suggestedText}`
        : view.suggestedText
      this.$store.dispatch('cvTextArtifacts/setLastProgrammaticValue', {
        offerId: this.cvOfferId, value: joined
      })
      this.manual_text = joined
      this.$store.dispatch('cvTextArtifacts/recordApplied', {
        offerId: this.cvOfferId,
        appliedText: view.suggestedText,
        appliedMode: view.reusedFromMode || this.currentSendMode,
        appliedKind: 'append',
        previousText: previousText,
        previousFlags: previousFlags
      })
      this.$store.dispatch('cvTextArtifacts/setExpanded', { offerId: this.cvOfferId, value: false })
    },
    onSuggestionUndo () {
      const snapshot = this.cvPanel.undoSnapshot
      if (!snapshot) return
      const previousText = snapshot.previousText || ''
      this.$store.dispatch('cvTextArtifacts/setLastProgrammaticValue', {
        offerId: this.cvOfferId, value: previousText
      })
      this.manual_text = previousText
      this.$store.dispatch('cvTextArtifacts/recordUndo', this.cvOfferId)
    },
    onSuggestionRegenerate (kind) {
      this.$store.dispatch('cvTextArtifacts/regenerate', {
        offerId: this.cvOfferId, mode: this.currentSendMode, kind: kind, vm: this
      })
    },
    onManualTextChanged (newVal) {
      if (!this.cvOfferId) return
      const panel = this.cvPanel
      if (panel.lastProgrammaticValue !== null && newVal === panel.lastProgrammaticValue) return
      if (panel.undoDone) {
        this.$store.dispatch('cvTextArtifacts/clearSuggestionTelemetry', this.cvOfferId)
        return
      }
      const applied = panel.applied
      if (!applied) return
      const normalize = value => (value || '').replace(/\s+/g, ' ').trim()
      const stillApplied = applied.appliedKind === 'use'
        ? (newVal || '').trim() === (applied.appliedText || '').trim()
        : normalize(newVal).includes(normalize(applied.appliedText))
      if (stillApplied) return
      this.$store.dispatch('cvTextArtifacts/clearSuggestionTelemetry', this.cvOfferId)
    },
    // FOUR additive params. Every pre-existing key stays byte-unchanged (D11).
    //
    // `auto_selected_files` is deliberately NOT sent — the server recomputes it
    // from files_sent through Cv::FileTypeClassifier, so a client-supplied copy
    // was a second source of truth for the very comparison the shadow report
    // exists to make.
    cvEventParams () {
      const telemetry = this.cvTelemetry
      return {
        send_mode: this.currentSendMode,
        suggestion_applied: telemetry.suggestion_applied === true,
        suggestion_source_mode: telemetry.suggestion_source_mode,
        suggestion_text: telemetry.suggestion_text || ''
      }
    },
    setQuestionAnswer (question) {
      this.questions_answers[question.designator] = question
      this.setIsSubmitable()
    },
    getTitle () {
      let company_position_id =
        this.company_position_id_content
          ? ' - ( משרה מספר: ' + this.company_position_id_content + ' )'
          : ''

      // For super lite mode, use stat_id instead of candidate name and number
      if (this.is_lite && this.stat_id) {
        return (
          this.$settings.get({ path: 'app_name' }) +
          ': ' +
          this.cv_offer.position.company.name +
          ' - A-' +
          this.stat_id +
          ' - ' +
          this.cv_offer.position.title +
          company_position_id
        )
      } else if (this.is_lite) {
        // Fallback if stat_id is not available yet
        return (
          this.$settings.get({ path: 'app_name' }) +
          ': ' +
          this.cv_offer.position.company.name +
          ' - A-' +
          'LOADING' +
          ' - ' +
          this.cv_offer.position.title +
          company_position_id
        )
      } else {
        let discrete_text = this.cv_offer.applicant.is_discrete ? 'דיסקרטי - ' : ''
        return (
          this.$settings.get({ path: 'app_name' }) +
          ': ' +
          this.cv_offer.position.company.name +
          ' - ' +
          `${this.cv_offer.applicant.first_name} ${this.cv_offer.applicant.last_name}` +
          ' - ' +
          discrete_text +
          this.cv_offer.position.title.replace('עו"ד ל', '') +
          company_position_id
        )
      }
    },
    previewApplicantProfile () {
      this.create({
        designator: 'applicant-profile-modal',
        component_name: 'ApplicantProfilePdf',
        applicant_id: this.cv_offer.applicant.resource_id,
        modal_title: 'Preview - פרופיל מועמד',
        modal_width: 1000,
        is_center_modal: true,
        manual_text: this.manual_text,
        sections_visibility: this.applicant_profile_sections_visibility
      })
    },
    updatePositionContactPhones (newPhones) {
      // Update position's contact_phones field when whatsapp_phones changes
      this.$caller.request('put', `/positions/${this.cv_offer.position.id}`, 'admin_headers', {
        contact_phones: newPhones
      }).then(() => {
        // Update local data to reflect the change
        this.cv_offer.position.contact_phones = newPhones
      }).catch(error => {
        console.error('Failed to update position contact phones:', error)
        this.$utils.emit('show_toast', { text: 'שגיאה בעדכון מספרי הטלפון של המשרה' })
      })
    },
    validateAndNormalizePhones () {
      // Clear any previous error
      this.whatsapp_phones_error = ''

      // If field is empty, that's valid
      if (!this.whatsapp_phones || this.whatsapp_phones.trim().length === 0) {
        return
      }

      // Normalize the field (remove spaces)
      const normalizedPhones = this.validator.normalizePhoneNumbers(this.whatsapp_phones)
      this.whatsapp_phones = normalizedPhones

      // Validate the normalized phones
      const isValid = this.validator.validateMultiplePhoneNumbers({ value: normalizedPhones })

      if (!isValid) {
        // Extract error message from the validator (it shows an alert, but we want to show inline error)
        const phones = normalizedPhones.split(',').filter(phone => phone.length > 0)
        const invalidPhones = []
        const israeliPhoneRegex = /^05\d{8}$/

        phones.forEach(phone => {
          if (!israeliPhoneRegex.test(phone.trim())) {
            invalidPhones.push(phone)
          }
        })

        if (invalidPhones.length > 0) {
          this.whatsapp_phones_error = `מספרי הטלפון הבאים לא תקינים: ${invalidPhones.join(', ')}`
        }
      }
    },
    async togglePhoneBookEdit () {
      if (this.isEditingPhoneBook) {
        // Save the phone book
        try {
          await this.$network_utils.updateCompany({
            id: this.cv_offer.position.company.resource_id,
            phone_book: this.company_phone_book
          })

          // Update local data to reflect the changes
          this.cv_offer.position.company.phone_book = this.company_phone_book

          this.$utils.emit('show_toast', { text: 'ספר הטלפונים עודכן בהצלחה' })
          this.isEditingPhoneBook = false
        } catch (error) {
          this.$utils.emit('show_toast', { text: 'שגיאה בעדכון ספר הטלפונים' })
          console.error('Error updating phone book:', error)
        }
      } else {
        // Enter edit mode
        this.isEditingPhoneBook = true
      }
    },
    onSendModeChange (mode) {
      // Update the local state based on the selected mode
      switch (mode) {
        case 'normal':
          this.is_lite = false
          break
        case 'lite':
          this.is_lite = true
          break
        case 'super_lite':
          this.is_lite = true
          break
        default:
          this.is_lite = true
      }

      // Update the current mode
      this.currentSendMode = mode

      // Trigger any necessary updates
      this.setIsSubmitable()
    },
    async loadMixedAbstract () {
      try {
        const response = await this.$caller.request(
          'get',
          `/abstract_picker/get_mixed?offer_id=${this.cv_offer.id}`,
          'admin_headers'
        )

        if (response && response.abstract) {
          this.mixed_abstract = response.abstract
          this.mixed_abstract_id = response.id
        } else {
          // If no abstract available, show placeholder
          this.mixed_abstract = 'אין אבסטרקט זמין - יש לייצר חדש'
          this.mixed_abstract_id = null
        }
      } catch (error) {
        console.error('Error loading mixed abstract:', error)
        this.mixed_abstract = 'שגיאה בטעינת האבסטרקט המעורב'
        this.mixed_abstract_id = null
      }
    },
    async saveMixedAbstract () {
      try {
        if (this.mixed_abstract_id) {
          // Update existing abstract
          await this.$caller.request(
            'put',
            `/applicant_abstracts/${this.mixed_abstract_id}`,
            'admin_headers',
            {
              content: this.mixed_abstract
            }
          )
        } else {
          // Create new abstract
          const response = await this.$caller.request(
            'post',
            '/applicant_abstracts',
            'admin_headers',
            {
              applicant_id: this.cv_offer.applicant.resource_id,
              content: this.mixed_abstract,
              version: 1 // This will be handled by the backend
            }
          )

          // Store the new ID for future updates
          if (response && response.id) {
            this.mixed_abstract_id = response.id
          }
        }

        this.$utils.emit('show_toast', { text: 'האבסטרקט המעורב נשמר בהצלחה' })
      } catch (error) {
        console.error('Error saving mixed abstract:', error)
        this.$utils.emit('show_toast', { text: 'שגיאה בשמירת האבסטרקט המעורב' })
      }
    },
    toggleAbstractEdit () {
      this.isEditingAbstract = !this.isEditingAbstract
    },
    async saveAbstract () {
      try {
        await this.$network_utils.updateApplicant({
          applicant_id: this.cv_offer.applicant.resource_id,
          abstract: this.abstract,
          active_at: new Date()
        })

        this.$utils.emit('show_toast', { text: 'האבסטרקט נשמר בהצלחה' })
        this.isEditingAbstract = false
      } catch (error) {
        console.error('Error saving abstract:', error)
        this.$utils.emit('show_toast', { text: 'שגיאה בשמירת האבסטרקט' })
      }
    },
    onMixedAbstractIdChanged (abstractId) {
      // Store the mixed abstract ID for use when sending emails
      this.mixed_abstract_id = abstractId

      // If we received a valid abstract ID, automatically switch to mixed abstract type
      if (abstractId) {
        this.selectedAbstractType = 'mixed'
      }
    },
    selectAbstractType (type) {
      this.selectedAbstractType = type
    },
    ...mapMutations('modals', ['create', 'remove', 'setDirtyGuard', 'clearDirtyGuard'])
  }
}
</script>
<style scoped>
.lite_abstract{
  border-color: black;
  border-width: 3px;
}
.abstract {
  text-align: right;
  white-space: pre-line;
  line-height: 24px;
  direction: rtl;
  padding: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: #f9f9f9;
  margin: 15px 0;
}
.abstract-textarea {
  width: 100%;
  min-height: 120px;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
  direction: rtl;
  text-align: right;
}
.abstract-textarea:focus {
  outline: none;
  border-color: #3273dc;
  box-shadow: 0 0 0 2px rgba(50, 115, 220, 0.2);
}
.abstract-display {
  min-height: 120px;
  padding: 8px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: #fff;
  direction: rtl;
  text-align: right;
}
.abstract-content {
  white-space: pre-line;
  line-height: 24px;
  font-size: 14px;
}
.abstract-placeholder {
  color: #999;
  font-style: italic;
  font-size: 14px;
}
.is_not_submitable {
  opacity: 0.5;
  pointer-events: none;
}
textarea {
  min-height: 120px;
  min-width: 100% !important;
}
input {
  min-width: 100% !important;
}
.applicant_profile_checkbox {
  margin-left: 5px;
  margin-top: 5px;
  margin-bottom: 5px;
}
.submit-disabled-reasons {
  text-align: center;
  max-width: 300px;
}
.disabled-reason {
  margin-bottom: 3px;
  padding: 2px 8px;
  background-color: rgba(255, 0, 0, 0.1);
  border-radius: 3px;
  border: 1px solid rgba(255, 0, 0, 0.2);
}
.mixed-abstract-section {
  margin: 15px 0;
  padding: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: #f9f9f9;
}
.mixed-abstract-textarea {
  width: 100%;
  min-height: 120px;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
  direction: rtl;
  text-align: right;
}
.mixed-abstract-textarea:focus {
  outline: none;
  border-color: #3273dc;
  box-shadow: 0 0 0 2px rgba(50, 115, 220, 0.2);
}
.mixed-abstract-display {
  min-height: 120px;
  padding: 8px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: #fff;
  direction: rtl;
  text-align: right;
}
.mixed-abstract-content {
  white-space: pre-line;
  line-height: 24px;
  font-size: 14px;
}
.mixed-abstract-placeholder {
  color: #999;
  font-style: italic;
  font-size: 14px;
}

.abstract-selection-section {
  margin: 15px 0;
  padding: 15px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #f9f9f9;
}

.abstract-selection-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
}

.abstract-type-cards {
  display: flex;
  gap: 15px;
  margin-top: 10px;
}

.abstract-card {
  flex: 1;
  padding: 15px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  background-color: #fff;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.abstract-card:hover {
  border-color: #3273dc;
  box-shadow: 0 2px 8px rgba(50, 115, 220, 0.1);
}

.abstract-card.is-selected {
  border-color: #3273dc;
  background-color: #f0f7ff;
  box-shadow: 0 2px 8px rgba(50, 115, 220, 0.2);
}

.card-header {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.card-radio {
  margin-left: 10px;
  margin-right: 0;
  cursor: pointer;
}

.card-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
  color: #333;
}

.card-title i {
  color: #3273dc;
}

.card-description {
  font-size: 13px;
  color: #666;
  margin-bottom: 10px;
  line-height: 1.4;
}

.card-status {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 500;
}

.abstract-preview-section {
  margin-top: 20px;
  padding: 15px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #fff;
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e0e0e0;
}

.preview-header .subtitle {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
}

.abstract-preview-content {
  min-height: 120px;
}

.abstract-display {
  min-height: 120px;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background-color: #fafafa;
  direction: rtl;
  text-align: right;
  cursor: pointer;
  transition: all 0.2s ease;
}

.abstract-display:hover {
  background-color: #f0f0f0;
  border-color: #3273dc;
}

.abstract-content {
  white-space: pre-line;
  line-height: 1.6;
  font-size: 14px;
  color: #333;
}

.abstract-placeholder {
  color: #999;
  font-style: italic;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100px;
  text-align: center;
  gap: 8px;
}

.abstract-placeholder i {
  color: #3273dc;
}

.abstract-edit {
  border: 1px solid #3273dc;
  border-radius: 6px;
  overflow: hidden;
}

.radio-option {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 14px;
}

.radio-option input[type="radio"] {
  margin-left: 8px;
  margin-right: 0;
  cursor: pointer;
}

.radio-option span {
  cursor: pointer;
  user-select: none;
}

/* Enhanced Abstract Selection Styles */
.enhanced-abstract-selection {
  margin: 20px 0;
  padding: 20px;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  background: linear-gradient(135deg, #f8f9ff 0%, #f0f7ff 100%);
  direction: rtl;
  text-align: right;
}

.selection-header {
  margin-bottom: 20px;
}

.selection-title {
  font-size: 18px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  direction: rtl;
}

.selection-title i {
  color: #3273dc;
  font-size: 20px;
}

.abstract-type-cards {
  display: flex;
  gap: 20px;
  margin-top: 15px;
  direction: rtl;
}

.abstract-card {
  flex: 1;
  padding: 20px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  background-color: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  direction: rtl;
  text-align: right;
}

.abstract-card:hover {
  border-color: #3273dc;
  box-shadow: 0 4px 12px rgba(50, 115, 220, 0.15);
  transform: translateY(-2px);
}

.abstract-card.is-selected {
  border-color: #3273dc;
  background: linear-gradient(135deg, #f0f7ff 0%, #e8f4ff 100%);
  box-shadow: 0 4px 16px rgba(50, 115, 220, 0.25);
  transform: translateY(-2px);
}

.card-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  direction: rtl;
}

.card-radio {
  margin-left: 12px;
  margin-right: 0;
  cursor: pointer;
  transform: scale(1.2);
}

.card-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  font-size: 16px;
  color: #2c3e50;
  direction: rtl;
}

.card-title i {
  color: #3273dc;
  font-size: 18px;
}

.card-description {
  font-size: 14px;
  color: #666;
  margin-bottom: 15px;
  line-height: 1.5;
  direction: rtl;
  text-align: right;
}

.card-status {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  direction: rtl;
}

.card-status .fas.fa-circle {
  font-size: 10px;
}

.text-success {
  color: #28a745;
}

.text-muted {
  color: #6c757d;
}

/* Abstract Fields Section */
.abstract-fields-section {
  margin: 25px 0;
  direction: rtl;
}

.abstract-field-container {
  margin-bottom: 25px;
  padding: 20px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  background-color: #ffffff;
  transition: all 0.3s ease;
  direction: rtl;
  text-align: right;
}

.abstract-field-container.is-active {
  border-color: #3273dc;
  background: linear-gradient(135deg, #f0f7ff 0%, #ffffff 100%);
  box-shadow: 0 4px 12px rgba(50, 115, 220, 0.15);
}

.abstract-field-container.is-inactive {
  opacity: 0.7;
  background-color: #f8f9fa;
  border-color: #dee2e6;
}

.abstract-field-container.is-inactive:hover {
  opacity: 0.9;
  border-color: #3273dc;
  cursor: pointer;
}

.field-header {
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e0e0e0;
  direction: rtl;
}

.field-title {
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  direction: rtl;
}

.field-title i {
  color: #3273dc;
  margin-left: 8px;
  font-size: 16px;
}

.active-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #28a745;
  font-weight: 600;
}

.active-indicator i {
  color: #28a745;
  font-size: 14px;
}

.field-content {
  transition: all 0.3s ease;
  direction: rtl;
}

.abstract-field-container.is-inactive .field-content {
  pointer-events: none;
}

.abstract-field-container.is-inactive .field-content:hover {
  pointer-events: auto;
}

/* RTL Support */
[dir="rtl"] .enhanced-abstract-selection,
[dir="rtl"] .abstract-type-cards,
[dir="rtl"] .abstract-card,
[dir="rtl"] .card-header,
[dir="rtl"] .card-title,
[dir="rtl"] .card-description,
[dir="rtl"] .card-status,
[dir="rtl"] .abstract-fields-section,
[dir="rtl"] .abstract-field-container,
[dir="rtl"] .field-header,
[dir="rtl"] .field-title,
[dir="rtl"] .field-content {
  direction: rtl;
  text-align: right;
}

/* Responsive Design */
@media (max-width: 768px) {
  .abstract-type-cards {
    flex-direction: column;
    gap: 15px;
  }

  .abstract-card {
    padding: 15px;
  }

  .enhanced-abstract-selection {
    padding: 15px;
  }

  .abstract-field-container {
    padding: 15px;
  }
}

/* Animation for smooth transitions */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.abstract-field-container.is-active {
  animation: fadeInUp 0.3s ease-out;
}
</style>
